import axios from "axios";
import { apiClient } from "./client";
import { fieldMessagesOf, readErrorEnvelope } from "./errorCodes";
import { isPaginated } from "./pagination";
import { scopeHeaders, type ListScope } from "./distributors";

import type { LLMCredentialProvider } from "@/lib/constants/providerTypes";
import type { components } from "./schema";

/** `StandardResultsSetPagination.max_page_size` on the backend. */
const MAX_PAGE_SIZE = 100;
const MAX_PAGES = 100;

export type ProviderType = "cloud" | "llm_api";
export type CloudProvider = "aws" | "azure" | "gcp";
export type CredentialProvider = CloudProvider | LLMCredentialProvider;

/**
 * Whose price this account's cost figures already are.
 *
 * `retail` is a customer's own cloud account, billed at provider list price.
 * `partner` is a CSP subscription under a reseller tenant, whose API already
 * returns the reseller's discounted price. Resale rates read this to know which
 * way their percentage points: see `src/features/resale/rateFactor.ts`.
 *
 * Tied to the generated enum so a backend change fails compilation here rather
 * than drifting.
 */
export type PriceBasis = components["schemas"]["PriceBasisEnum"];

/** This account's basis, and the one the accounts beside it agree on instead. */
export type BasisDivergence = components["schemas"]["BasisDivergence"];

/** One step of a multi-step validation (AWS auth/resources/cost). */
export interface CredentialStep {
  ok: boolean;
  detail?: string;
}

/** Provider identity + currency, verify detail, and step results from the last test/validate. */
export interface CredentialDiscovered {
  account_id?: string;
  subscription_id?: string;
  tenant_id?: string;
  project_id?: string;
  organization?: string;
  currency?: string;
  last_tested?: string;
  steps?: Record<string, CredentialStep>;
  provider?: string;
  verify_status?: string;
  message?: string;
}

/**
 * Azure service-principal detail carried on the list response.
 *
 * The nested `azure_credential` object was already on the wire (the API's
 * `AzureCredentialSerializer` includes it); this type simply never declared it,
 * so `expiration_date` was invisible to the app. A secret that expires stops
 * cost data without any other signal, and unsurfaced that reads as "this
 * account spent nothing".
 */
export interface AzureCredentialDetail {
  subscription_id?: string;
  tenant_id?: string;
  client_id?: string;
  /** ISO date the service-principal secret expires. Null when not recorded. */
  expiration_date?: string | null;
}

/** A cloud or AI credential's id: an integer, as the API sends it. */
export type CredentialId = components["schemas"]["CloudCredential"]["id"];

/**
 * Who last decided the basis, when, and under which agreement, typed by the
 * contract. The server omits all four for a caller below MSP admin, and the
 * contract marks them optional for that reason, so nothing is shown for one it
 * did not send. `price_basis_set_by_name` is a person only when they are in the
 * caller's own branch; otherwise it is their organization or "The platform".
 */
type BasisProvenance = Pick<
  components["schemas"]["CloudCredential"],
  | "price_basis_set_by"
  | "price_basis_set_by_name"
  | "price_basis_set_at"
  | "price_basis_note"
>;

export interface Credential extends BasisProvenance {
  id: CredentialId;
  nickname: string;
  provider: string;
  azure_credential?: AzureCredentialDetail | null;
  /** Present on newer API responses; infer from provider when missing. */
  provider_type?: ProviderType;
  /**
   * Owning company UUID.
   *
   * This was `number | string` with a note that the live API sent an integer FK.
   * That stopped being true when companies became UUID-keyed: the contract now
   * says `company: string` (format uuid) everywhere it appears, here and in
   * `company_breakdown[].company_id`. The union outlived the reason for it, and
   * a union that no longer varies just weakens every comparison against it.
   */
  company: string;
  company_external_id?: string;
  status: string;
  is_expired?: boolean;
  currency: string;
  /**
   * Optional on the contract, and left that way here. A row the API reports no
   * basis for is not a row that disagrees with its siblings, which is the
   * distinction `unanimousBasis` turns on.
   */
  price_basis?: PriceBasis;
  /**
   * Null when the basis agrees with its siblings, or for a caller below MSP
   * admin. Always sent, but optional here, as `price_basis` is, so a row built
   * without it is not read as one that disagrees.
   */
  price_basis_divergence?: BasisDivergence | null;
  last_discovery: string | null;
  last_cost_sync: string | null;
  /**
   * When an inventory sync last completed. Null means it has never run, which
   * is not the same as running and finding nothing: the first means wait, the
   * second means the account really is empty.
   */
  last_inventory_sync?: string | null;
  last_metrics_sync: string | null;
  discovered?: CredentialDiscovered;
  created: string;
  modified: string;
}

export interface CreateCredentialRequest {
  nickname: string;
  provider: CredentialProvider;
  provider_type?: ProviderType;
  currency?: string;
  price_basis?: PriceBasis;
  aws_credential?: { role_arn: string };
  azure_credential?: {
    subscription_id: string;
    tenant_id: string;
    client_id: string;
    client_secret: string;
    expiration_date?: string;
  };
  gcp_credential?: {
    project_id: string;
    service_account_key: Record<string, unknown>;
  };
  // LLM / API providers (single management key, matches Cursor/OpenRouter pattern)
  openai_credential?: { api_key: string };
  anthropic_credential?: { api_key: string };
  cursor_credential?: { api_key: string };
  openrouter_credential?: { api_key: string };
}

export interface UpdateCredentialRequest {
  nickname?: string;
  currency?: string;
  /** LLM keys only: may Desk make model calls with it (spend tracking otherwise). */
  allow_inference?: boolean;
  price_basis?: PriceBasis;
  /** Which agreement the basis comes from. The server requires it when the
   *  basis changes on an account that already has cost on file. */
  price_basis_note?: string;
}

export interface CredentialValidationResult {
  valid: boolean;
  reason?: string;
  message?: string;
  info?: CredentialDiscovered;
}

export interface AwsOnboarding {
  external_id: string;
  platform_account_id: string;
  trust_policy: Record<string, unknown>;
  iam_policy: Record<string, unknown>;
  cloudformation_template: string;
}

export interface AzureOnboarding {
  app_name: string;
  sp_command: string;
  roles: string[];
  required_actions: string[];
}

/** AI provider catalog entry (supported vs coming soon). */
export interface AiProviderInfo {
  provider: string;
  label: string;
  key_field: string;
  status: "supported" | "coming_soon";
  capabilities: string[];
}

/** One model row in the LLM analytics breakdown (mirrors SDK ModelBreakdownRow). */
export interface LlmModelBreakdown {
  model: string;
  cost: number;
  /**
   * False when every line for this model carried no billed cost of its own (a
   * provider that reports no money, or a usage estimate): its `cost` of 0 is
   * not a price.
   */
  cost_reported: boolean;
  requests: number;
  input_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  cache_read_tokens: number;
}

/** An insight surfaced by the SDK analytics engine (mirrors InsightFinding). */
export interface LlmFinding {
  rule_id: string;
  severity: "info" | "low" | "medium" | "high";
  title: string;
  message: string;
  estimated_monthly_impact_usd?: number | null;
  suggested_action?: string;
}

/**
 * LLM analytics result: spend breakdown + rule-based findings.
 *
 * Hand-written: the endpoint declares no response schema, so the generated
 * types carry nothing for it.
 */
export interface LlmAnalytics {
  breakdown: {
    total_cost: number;
    /** False when nothing in the window carried a billed cost: 0 is "not reported", not "free". */
    total_cost_reported: boolean;
    total_requests: number;
    total_input_tokens: number;
    total_output_tokens: number;
    total_reasoning_tokens: number;
    by_model: LlmModelBreakdown[];
    by_project: {
      project_id: string;
      cost: number;
      requests: number;
      cost_reported: boolean;
    }[];
    daily: Record<string, number>;
  };
  findings: LlmFinding[];
  /** The billing currency every figure is in; null when the account holds no rows and has none set. */
  currency: string | null;
  /** Lines with usage but no billed cost of their own; they add nothing to any figure. */
  unpriced_rows: number;
  /** Lines billed in another currency, left out of every figure, counted per currency. */
  excluded_rows?: Record<string, number>;
}

/** One FOCUS-shaped cost row (broad spec — kept as a loose record). */
export type FocusRow = Record<string, unknown>;

const VALIDATION_STATE_CODES = [400, 401, 422];

/** One sentence for several field messages: each named, said once. */
function joinFieldMessages(pairs: [string | undefined, string][]): string {
  if (pairs.length === 1) return pairs[0][1];
  return [...new Set(pairs.map(([f, m]) => (f ? `${f}: ${m}` : m)))].join(
    "; ",
  );
}

/**
 * Turn a backend error into one human sentence, read through
 * {@link readErrorEnvelope}: a validation refusal's field messages (one reads
 * as its sentence, several keep a `field: ` prefix), otherwise the envelope's
 * own sentence. A body with no envelope is a view's own answer: its `detail`
 * when it states one, else the field map it is.
 *
 * The flat `detail` is read first because such a body can carry data beside
 * it: sync's 409 for an unvalidated account sends `status` and `is_expired`,
 * and a boolean is not a field message, so the body read as no field map at
 * all and the reader got "Request failed with status code 409".
 */
export function formatCredentialApiError(error: unknown): string {
  if (axios.isAxiosError(error) && error.response?.data) {
    const envelope = readErrorEnvelope(error);
    if (envelope) {
      if (envelope.fieldRefusal) {
        return joinFieldMessages(envelope.fieldMessages);
      }
      if (envelope.message) return envelope.message;
    } else {
      const detail = (error.response.data as { detail?: unknown }).detail;
      if (typeof detail === "string" && detail.trim() !== "") return detail;
      const pairs = fieldMessagesOf(error.response.data);
      if (pairs.length > 0) return joinFieldMessages(pairs);
    }
  }
  return error instanceof Error ? error.message : "Request failed";
}

export const credentialsApi = {
  /**
   * Every credential the caller can see, across all pages. `/credentials/` is
   * paginated (PAGE_SIZE 25, max 100); this walks `next` to exhaustion instead
   * of returning only page 1. Narrowing is by `X-Context-*` scope headers only,
   * so filter client-side (the old `company_id`/`msp_id` args were dropped).
   *
   * The walk is capped at `MAX_PAGES`: a malformed or self-referential `next`
   * would otherwise loop forever and hang the tab. Hitting the cap warns and
   * returns what was collected rather than throwing.
   *
   * `opts.scope` is the navigator escape hatch the org lists already use. A
   * list whose job is "which credentials could this group name" must not be
   * filtered by the org the viewer happens to be looking at, or an MSP admin
   * drilled into one company sees none of the others. `"all"` is refused with
   * 403 for every role below platform admin, so callers pass `"branch"` there
   * and get their own organization and everything under it.
   */
  list: async (
    params?: {
      provider?: string;
      provider_type?: ProviderType;
    },
    opts?: { scope?: ListScope },
  ): Promise<Credential[]> => {
    const rows: Credential[] = [];
    const headers = scopeHeaders(opts?.scope);

    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const response = await apiClient.get("/credentials/", {
        params: { ...params, page, page_size: MAX_PAGE_SIZE },
        ...(headers ? { headers } : {}),
      });
      const data = response.data;

      if (!isPaginated<Credential>(data)) {
        // Merge rather than replace: returning `data` here would silently drop
        // every row already collected from earlier pages.
        if (Array.isArray(data)) rows.push(...(data as Credential[]));
        return rows;
      }

      rows.push(...data.results);
      if (!data.next) return rows;
    }

    console.warn(
      `credentialsApi.list stopped at the ${MAX_PAGES}-page cap with ${rows.length} rows; the result may be incomplete.`,
    );
    return rows;
  },

  get: async (id: CredentialId): Promise<Credential> => {
    const response = await apiClient.get(`/credentials/${id}/`);
    return response.data;
  },

  create: async (data: CreateCredentialRequest): Promise<Credential> => {
    const response = await apiClient.post("/credentials/", data);
    return response.data;
  },

  update: async (
    id: CredentialId,
    data: UpdateCredentialRequest,
  ): Promise<Credential> => {
    const response = await apiClient.patch(`/credentials/${id}/`, data);
    return response.data;
  },

  delete: async (id: CredentialId): Promise<void> => {
    await apiClient.delete(`/credentials/${id}/`);
  },

  validate: async (id: CredentialId): Promise<CredentialValidationResult> => {
    try {
      const response = await apiClient.post(`/credentials/${id}/validate/`);
      return { valid: true, ...response.data };
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response &&
        VALIDATION_STATE_CODES.includes(error.response.status)
      ) {
        const data = (error.response.data ??
          {}) as Partial<CredentialValidationResult> & {
          error?: {
            code?: string;
            message?: string;
            details?: { steps?: CredentialDiscovered["steps"] };
          };
        };
        // The backend attaches the failed capability matrix to the envelope's
        // `details`; surface it as `info.steps` so the caller can name the failed
        // step in the toast right away. (The persisted card matrix itself still
        // refreshes from `cred.discovered` on the post-validate refetch.)
        const info =
          data.info ??
          (data.error?.details?.steps
            ? { steps: data.error.details.steps }
            : undefined);
        return {
          valid: false,
          // Flat body first (legacy/tests), then the DRF `{ error: {...} }`
          // envelope the backend actually sends for raised exceptions.
          reason: data.reason ?? data.error?.code,
          message: data.message ?? formatCredentialApiError(error),
          info,
        };
      }
      throw error;
    }
  },

  /**
   * Trigger an immediate cost sync.
   *
   * `granularity` is the SDK's core vocabulary, and the backend rejects
   * anything else with a 400. It used to be `"day" | "hour" | "minute"`
   * defaulting to `"day"` — those are the bucket labels `AISpendRecord`
   * *stores*, never an input. The backend and SDK were moved onto the core
   * names; this caller was not, so every LLM credential sync from the UI
   * returned `granularity must be one of: hourly, daily, weekly, monthly,
   * yearly`. Cloud credentials never noticed, because their branch ignores
   * the field entirely.
   */
  /**
   * Start a cost sync.
   *
   * Without a window the task uses its own trailing default -- 90 days for a
   * cloud credential, **7 days** for an AI one -- which is right for the
   * scheduled run and useless for a backfill. `start_date`/`end_date` ask for
   * a specific period instead; the response echoes the window it started, so
   * the caller reports what actually ran rather than what it assumed.
   *
   * The server refuses a malformed, backwards, future or over-long window with
   * a 400 rather than quietly falling back to the default.
   */
  sync: async (
    id: CredentialId,
    options: {
      granularity?: "hourly" | "daily" | "weekly" | "monthly" | "yearly";
      /** ISO date. Omit both for the task's default trailing window. */
      start_date?: string;
      end_date?: string;
    } = {},
  ): Promise<{
    status: string;
    message?: string;
    start_date?: string;
    end_date?: string;
  }> => {
    const { granularity = "daily", start_date, end_date } = options;
    const response = await apiClient.post(`/credentials/${id}/sync/`, {
      granularity,
      ...(start_date ? { start_date } : {}),
      ...(end_date ? { end_date } : {}),
    });
    return response.data;
  },

  test: async (id: CredentialId): Promise<{ success: boolean }> => {
    const response = await apiClient.post(`/credentials/${id}/test/`);
    return response.data;
  },

  /** AWS onboarding assets (trust/IAM policy + CloudFormation) for the scope. */
  awsOnboarding: async (): Promise<AwsOnboarding> => {
    const response = await apiClient.get("/credentials/aws-onboarding/");
    return response.data;
  },

  /** Azure SP-creation guidance (command + roles + required actions). */
  azureOnboarding: async (
    subscriptionId?: string,
  ): Promise<AzureOnboarding> => {
    const response = await apiClient.get("/credentials/azure-onboarding/", {
      params: subscriptionId ? { subscription_id: subscriptionId } : undefined,
    });
    return response.data;
  },

  /** List every Azure subscription a service principal can access. */
  discoverAzureSubscriptions: async (body: {
    tenant_id: string;
    client_id: string;
    client_secret: string;
  }): Promise<AzureDiscoveredSubscription[]> => {
    const response = await apiClient.post(
      "/credentials/discover-azure-subscriptions/",
      body,
    );
    return response.data;
  },

  /** Create one credential per selected Azure subscription (shared SP). */
  bulkAzureSubscriptions: async (body: {
    tenant_id: string;
    client_id: string;
    client_secret: string;
    expiration_date?: string;
    /** Applies to every subscription created. Omitted, each starts at Retail. */
    price_basis?: PriceBasis;
    subscriptions: { subscription_id: string; nickname?: string }[];
  }): Promise<AzureBulkCreateResult> => {
    const response = await apiClient.post(
      "/credentials/bulk-azure-subscriptions/",
      body,
    );
    return response.data;
  },

  /** AI provider catalog — supported vs coming-soon. */
  aiProviders: async (): Promise<AiProviderInfo[]> => {
    const response = await apiClient.get("/credentials/ai-providers/");
    return response.data;
  },

  /** LLM analytics (spend breakdown + findings) over the last `days` days. */
  analytics: async (id: CredentialId, days = 30): Promise<LlmAnalytics> => {
    const response = await apiClient.get(`/credentials/${id}/analytics/`, {
      params: { days },
    });
    return response.data;
  },

  /** FOCUS-shaped export for an LLM credential over the last `days` days. */
  focusExport: async (id: CredentialId, days = 30): Promise<FocusRow[]> => {
    const response = await apiClient.get(`/credentials/${id}/focus-export/`, {
      params: { days },
    });
    return response.data;
  },
};

export interface AzureDiscoveredSubscription {
  subscription_id: string;
  display_name: string;
  state: string;
  is_enabled: boolean;
  already_added: boolean;
}

export interface AzureBulkCreateResult {
  created: { id: number; subscription_id: string; nickname: string }[];
  skipped: string[];
}
